import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote, ChevronDown } from "lucide-react";

// Single source of truth for the social-proof block.
// Each testimonial is light, scannable, and rewritten in a polished
// review voice while preserving each person's underlying sentiment.
// `rating` defaults to 5 stars per buyer feedback policy.
const testimonials = [
  {
    name: "David Deutsch",
    role: "Direct-response copywriter",
    quote: "Beautiful work, and what a wonderful name. Everything about Calmasutras carries a quiet elegance that's rare to find in any product today.",
    initials: "DD",
    accent: "#a6c1a8",
    rating: 5,
  },
  {
    name: "Michelle Garrison",
    role: "Founder, Michelle Garrison Events",
    quote: "Gorgeous. I loved Calmasutras before I'd even read a single card. There's something instantly calming about it, almost magical.",
    initials: "MG",
    accent: "#96aeb4",
    rating: 5,
  },
  {
    name: "Sharanya Iyer",
    role: "Early reader",
    quote: "Beyond amazing. The design, the idea, the intention behind every card. Calmasutras is one of those rare projects you can't help but fall in love with.",
    initials: "SI",
    accent: "#d6a85f",
    rating: 5,
  },
  {
    name: "Dan Waldschmidt",
    role: "Author & Speaker",
    quote: "The rare project that earns your attention slowly and keeps it for life. A small daily ritual with surprisingly big returns.",
    initials: "DW",
    accent: "#587e62",
    rating: 5,
  },
  {
    name: "Varun Negandhi",
    role: "Founder, BeyondGrad",
    quote: "This is the kind of project I want to put in front of everyone I know. Bought a deck for my family the moment I saw it, and I'm proud to help spread the word.",
    initials: "VN",
    accent: "#a6c1a8",
    rating: 5,
  },
  {
    name: "Satish Grampurohit",
    role: "Cogniquest",
    quote: "Rajesh has a quiet way of building things that are both deeply meaningful and beautifully crafted. Calmasutras is exactly that.",
    initials: "SG",
    accent: "#c9b9a8",
    rating: 5,
  },
  {
    name: "Sameer Sortur",
    role: "Speaker & Mentor",
    quote: "Calmasutras feels like the kind of project that deserves to live in every quiet corner of the internet. I've shared it widely, and I'll keep doing so.",
    initials: "SS",
    accent: "#96aeb4",
    rating: 5,
  },
  {
    name: "Vinay Kumar",
    role: "Early reader",
    quote: "Stunning. The kind of project where you don't think twice before ordering. I can already feel the calm radiating off the cards.",
    initials: "VK",
    accent: "#a89b94",
    rating: 5,
  },
  {
    name: "Chidha Arunachalam",
    role: "Founder, CISOverse",
    quote: "What a wonderful gift idea. Ordering 20 decks for our next CISOverse event because nothing else I've seen captures the feeling of calm quite like this.",
    initials: "CA",
    accent: "#d6a85f",
    rating: 5,
  },
];

// Fisher-Yates shuffle. Called once per mount via useMemo so the order
// is fresh on every page load but stable while the user is on the page.
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function Stars({ count = 5 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          size={13}
          className={i < count ? "fill-[#d6a85f] text-[#d6a85f]" : "text-[#464646]/15"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function TestimonialCard({ t, index, hiddenOnMobile }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`relative bg-white rounded-2xl p-6 sm:p-7 border border-[#464646]/8 shadow-sm shadow-[#464646]/5 hover:shadow-lg hover:shadow-[#464646]/10 transition-shadow ${hiddenOnMobile ? "hidden md:block" : ""}`}
      data-testid={`testimonial-card-${index}`}
    >
      <Quote
        size={28}
        className="absolute -top-3 left-5 text-white fill-[#587e62] p-1 bg-[#587e62]"
        style={{ borderRadius: 8 }}
      />
      <Stars count={t.rating} />
      <blockquote className="mt-3 text-[15px] sm:text-base leading-relaxed text-[#464646]/90 font-serif italic">
        &ldquo;{t.quote}&rdquo;
      </blockquote>
      <figcaption className="mt-5 flex items-center gap-3">
        <span
          aria-hidden="true"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[12px] font-bold tracking-wider shrink-0"
          style={{ background: `linear-gradient(135deg, ${t.accent}, ${t.accent}cc)` }}
        >
          {t.initials}
        </span>
        <div className="min-w-0">
          <div className="text-[#464646] font-semibold text-sm leading-tight">{t.name}</div>
          <div className="text-[#464646]/55 text-[12px] leading-tight mt-0.5 truncate">{t.role}</div>
        </div>
      </figcaption>
    </motion.figure>
  );
}

export default function TestimonialsSection() {
  // Randomise once per mount. On every fresh page load mobile visitors
  // see a different "first 4". Desktop visitors see the full grid in a
  // different order too, which keeps the section feeling alive.
  const ordered = useMemo(() => shuffle(testimonials), []);
  const [expanded, setExpanded] = useState(false);

  return (
    <section
      id="testimonials"
      className="relative py-20 sm:py-28 md:py-32 px-5 md:px-10 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #fff8f3 0%, #f7efe6 55%, #fff8f3 100%)" }}
      data-testid="testimonials-section"
    >
      {/* Soft sage halo, top-left */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full opacity-50"
        style={{ background: "radial-gradient(circle, rgba(166,193,168,0.35) 0%, transparent 65%)" }}
      />
      {/* Soft mist halo, bottom-right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full opacity-50"
        style={{ background: "radial-gradient(circle, rgba(150,174,180,0.35) 0%, transparent 65%)" }}
      />

      <div className="max-w-[1200px] mx-auto relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-[#a6c1a8]/25 text-[#587e62] rounded-full px-4 py-1.5 mb-5 backdrop-blur">
            <Star size={12} className="fill-[#d6a85f] text-[#d6a85f]" />
            <span className="text-[11px] uppercase tracking-[0.3em] font-semibold">Early Reactions</span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-[#464646] leading-[1.05] tracking-tight max-w-4xl mx-auto">
            The first readers,
            <br />
            <span className="italic text-[#587e62]">in their own words.</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#464646]/65 max-w-2xl mx-auto leading-relaxed">
            Calmasutras is finding its way to writers, founders, and quiet thinkers around the world. Here's what they've said when they first held it.
          </p>
        </motion.div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {ordered.map((t, i) => (
            <TestimonialCard
              key={t.name}
              t={t}
              index={i}
              // first 4 are always visible (mobile + desktop). Cards 5+ are
              // hidden on mobile until "Load more" is tapped; always visible on md+.
              hiddenOnMobile={i >= 4 && !expanded}
            />
          ))}
        </div>

        {/* Mobile-only "Load more" button. Hidden on md+ (full grid is shown
            anyway), and hides itself on mobile once tapped. */}
        {!expanded && (
          <div className="mt-8 flex justify-center md:hidden">
            <button
              type="button"
              onClick={() => setExpanded(true)}
              data-testid="testimonials-load-more"
              className="inline-flex items-center gap-2 bg-[#464646] hover:bg-[#587e62] text-white rounded-full px-6 py-3 text-[13px] font-semibold tracking-wide transition-colors shadow-md shadow-[#464646]/15"
            >
              Load more reactions
              <ChevronDown size={15} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
